import { useMemo } from "react"
import { useTokenBalances } from "@/domains/api/hooks/use-token-balances"
import { useTokens } from "@/domains/api/hooks/use-tokens"
import { useApiService } from "@/hooks/use-api-service"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useNativeBalance } from "@/hooks/use-native-balance"
import { useNativeToken } from "@/hooks/use-native-token"
import { isAddressEqual } from "viem"
import { useAccount, useConfig } from "wagmi"

import { defaultTokenSort } from "@/domains/library"

/**
 * Use this whenever you want to display a list of tokens, either with or without their balances,
 * because it injects the native token into the list.
 *
 * Do NOT use `useTokens`
 */
export const useTokensWithBalances = () => {
  const apiService = useApiService()
  const nativeToken = useNativeToken()
  const { nativeBalance } = useNativeBalance()
  const chainConfig = useChainConfig()
  const { address } = useAccount()
  const config = useConfig()

  // Memoize query config
  const queryConfig = useMemo(
    () => ({
      apiService,
      refreshInterval: 1000 * 30,
      staleTime: 1000 * 30,
      cacheTime: 1000 * 300,
    }),
    [apiService],
  )

  const { tokens } = useTokens(queryConfig)

  // Memoize balances config
  const balancesConfig = useMemo(
    () => ({
      account: address,
      tokens,
      config,
      equalensAddress: chainConfig.equalizer.lenses.multiBalance,
    }),
    [address, tokens, config, chainConfig.equalizer.lenses.multiBalance],
  )

  const { balances } = useTokenBalances(balancesConfig)

  // Replace reducer with memoized computation
  const finalTokens = useMemo(() => {
    if (!tokens || !balances) return []

    const updatedTokens = tokens.map((token) => ({
      ...token,
      balance: balances[token.address]?.value ?? "0",
    }))

    // Handle native token
    const nativeTokenIndex = updatedTokens.findIndex((token) => isAddressEqual(nativeToken.address, token.address))

    if (nativeTokenIndex === -1) {
      updatedTokens.push({
        ...nativeToken,
        balance: nativeBalance?.value ?? "0",
      })
    } else {
      updatedTokens[nativeTokenIndex] = {
        ...nativeToken,
        balance: nativeBalance?.value ?? "0",
      }
    }

    return updatedTokens.sort(defaultTokenSort)
  }, [tokens, balances, nativeToken, nativeBalance])

  return { tokens: finalTokens }
}
